import { useState, useEffect } from "react";

// ── Color system ────────────────────────────────────────
const C = {
  teal:    "#0D9488",
  teal2:   "#14B8A6",
  mint:    "#CCFBF1",
  navy:    "#0D1B40",
  navy2:   "#1E3A5F",
  slate:   "#64748B",
  light:   "#F0FDFA",
  white:   "#FFFFFF",
  amber:   "#F59E0B",
  red:     "#EF4444",
  green:   "#10B981",
  border:  "#E2E8F0",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  body {
    font-family: 'DM Sans', sans-serif;
    background: ${C.light};
    min-height: 100vh;
    color: ${C.navy};
  }

  .app { min-height: 100vh; display: flex; flex-direction: column; }

  /* ── Navbar ── */
  .nav {
    background: ${C.white};
    border-bottom: 1px solid ${C.border};
    padding: 0 32px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .nav-logo {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 22px;
    color: ${C.teal};
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .nav-logo span { color: ${C.navy}; }
  .nav-badge {
    font-size: 10px;
    background: ${C.mint};
    color: ${C.teal};
    padding: 2px 8px;
    border-radius: 20px;
    font-weight: 600;
    letter-spacing: 0.05em;
  }

  /* ── Onboarding ── */
  .onboard-wrap {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
  }
  .onboard-card {
    background: ${C.white};
    border-radius: 20px;
    border: 1px solid ${C.border};
    padding: 48px;
    max-width: 600px;
    width: 100%;
    box-shadow: 0 4px 40px rgba(13,26,64,0.06);
  }
  .step-indicator {
    display: flex;
    gap: 8px;
    margin-bottom: 40px;
  }
  .step-dot {
    height: 4px;
    border-radius: 2px;
    flex: 1;
    background: ${C.border};
    transition: background 0.3s;
  }
  .step-dot.active { background: ${C.teal}; }
  .step-dot.done   { background: ${C.teal2}; }

  .step-label {
    font-size: 12px;
    font-weight: 600;
    color: ${C.teal};
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 12px;
  }
  .step-title {
    font-family: 'Syne', sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: ${C.navy};
    margin-bottom: 8px;
    line-height: 1.2;
  }
  .step-sub {
    font-size: 15px;
    color: ${C.slate};
    margin-bottom: 32px;
    line-height: 1.6;
  }

  /* ── Option cards ── */
  .options-grid {
    display: grid;
    gap: 12px;
    margin-bottom: 32px;
  }
  .option-card {
    border: 1.5px solid ${C.border};
    border-radius: 12px;
    padding: 16px 20px;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 14px;
    background: ${C.white};
  }
  .option-card:hover {
    border-color: ${C.teal2};
    background: ${C.light};
  }
  .option-card.selected {
    border-color: ${C.teal};
    background: ${C.mint};
  }
  .option-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${C.light};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
  }
  .option-card.selected .option-icon {
    background: ${C.white};
  }
  .option-title {
    font-family: 'Syne', sans-serif;
    font-weight: 600;
    font-size: 15px;
    color: ${C.navy};
    margin-bottom: 2px;
  }
  .option-desc {
    font-size: 13px;
    color: ${C.slate};
  }
  .option-check {
    margin-left: auto;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid ${C.border};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 12px;
    transition: all 0.2s;
  }
  .option-card.selected .option-check {
    background: ${C.teal};
    border-color: ${C.teal};
    color: white;
  }

  /* ── Data source grid ── */
  .source-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 32px;
  }
  .source-card {
    border: 1.5px solid ${C.border};
    border-radius: 12px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
    background: ${C.white};
  }
  .source-card:hover { border-color: ${C.teal2}; background: ${C.light}; }
  .source-card.selected { border-color: ${C.teal}; background: ${C.mint}; }
  .source-emoji { font-size: 28px; margin-bottom: 8px; }
  .source-title {
    font-family: 'Syne', sans-serif;
    font-weight: 600;
    font-size: 14px;
    color: ${C.navy};
    margin-bottom: 4px;
  }
  .source-sub { font-size: 12px; color: ${C.slate}; }

  /* ── Input ── */
  .input-group { margin-bottom: 20px; }
  .input-label {
    font-size: 13px;
    font-weight: 500;
    color: ${C.navy};
    margin-bottom: 6px;
    display: block;
  }
  .input-field {
    width: 100%;
    padding: 12px 16px;
    border: 1.5px solid ${C.border};
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    color: ${C.navy};
    background: ${C.white};
    outline: none;
    transition: border-color 0.2s;
  }
  .input-field:focus { border-color: ${C.teal}; }

  /* ── Upload zone ── */
  .upload-zone {
    border: 2px dashed ${C.border};
    border-radius: 12px;
    padding: 32px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    margin-bottom: 32px;
  }
  .upload-zone:hover { border-color: ${C.teal}; background: ${C.light}; }
  .upload-zone.uploaded { border-color: ${C.teal}; background: ${C.mint}; border-style: solid; }
  .upload-icon { font-size: 32px; margin-bottom: 10px; }
  .upload-title { font-family: 'Syne', sans-serif; font-weight: 600; font-size: 15px; color: ${C.navy}; margin-bottom: 4px; }
  .upload-sub { font-size: 13px; color: ${C.slate}; }

  /* ── Button ── */
  .btn-primary {
    background: ${C.teal};
    color: white;
    border: none;
    border-radius: 10px;
    padding: 14px 28px;
    font-family: 'Syne', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    width: 100%;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .btn-primary:hover { background: #0b7a70; transform: translateY(-1px); }
  .btn-primary:disabled { background: ${C.border}; color: ${C.slate}; cursor: not-allowed; transform: none; }

  /* ── Training screen ── */
  .training-wrap {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
  }
  .training-card {
    background: ${C.white};
    border-radius: 20px;
    border: 1px solid ${C.border};
    padding: 56px 48px;
    max-width: 500px;
    width: 100%;
    text-align: center;
    box-shadow: 0 4px 40px rgba(13,26,64,0.06);
  }
  .training-spinner {
    width: 64px;
    height: 64px;
    border: 4px solid ${C.mint};
    border-top-color: ${C.teal};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 28px;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .training-title {
    font-family: 'Syne', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: ${C.navy};
    margin-bottom: 10px;
  }
  .training-sub { font-size: 14px; color: ${C.slate}; margin-bottom: 32px; line-height: 1.6; }
  .progress-bar-bg {
    background: ${C.border};
    border-radius: 4px;
    height: 6px;
    overflow: hidden;
    margin-bottom: 12px;
  }
  .progress-bar-fill {
    height: 6px;
    background: linear-gradient(90deg, ${C.teal}, ${C.teal2});
    border-radius: 4px;
    transition: width 0.5s ease;
  }
  .progress-steps {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 28px;
    text-align: left;
  }
  .progress-step {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: ${C.slate};
  }
  .progress-step.done { color: ${C.teal}; }
  .progress-step.active { color: ${C.navy}; font-weight: 500; }
  .step-status {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 2px solid ${C.border};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    flex-shrink: 0;
  }
  .progress-step.done .step-status { background: ${C.teal}; border-color: ${C.teal}; color: white; }
  .progress-step.active .step-status { border-color: ${C.teal}; animation: pulse 1s ease infinite; }
  @keyframes pulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.2)} }

  /* ── Dashboard ── */
  .dash-wrap { flex: 1; padding: 28px 32px; }
  .dash-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }
  .dash-title {
    font-family: 'Syne', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: ${C.navy};
  }
  .dash-sub { font-size: 14px; color: ${C.slate}; margin-top: 2px; }
  .last-updated {
    font-size: 12px;
    color: ${C.slate};
    background: ${C.white};
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid ${C.border};
  }

  /* ── Metric cards ── */
  .metrics-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }
  .metric-card {
    background: ${C.white};
    border-radius: 14px;
    border: 1px solid ${C.border};
    padding: 20px;
  }
  .metric-label { font-size: 12px; color: ${C.slate}; margin-bottom: 8px; font-weight: 500; }
  .metric-value {
    font-family: 'Syne', sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: ${C.navy};
    margin-bottom: 4px;
  }
  .metric-delta {
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .delta-up   { color: ${C.green}; }
  .delta-down { color: ${C.red}; }
  .delta-neutral { color: ${C.slate}; }

  /* ── Two column layout ── */
  .dash-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }
  .dash-card {
    background: ${C.white};
    border-radius: 14px;
    border: 1px solid ${C.border};
    padding: 20px;
  }
  .card-title {
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: ${C.navy};
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* ── Bar chart ── */
  .bar-chart { display: flex; flex-direction: column; gap: 10px; }
  .bar-row { display: flex; align-items: center; gap: 10px; }
  .bar-label { font-size: 12px; color: ${C.slate}; width: 90px; flex-shrink: 0; text-align: right; }
  .bar-bg { flex: 1; background: ${C.light}; border-radius: 4px; height: 8px; overflow: hidden; }
  .bar-fill { height: 8px; border-radius: 4px; transition: width 0.8s ease; }
  .bar-value { font-size: 12px; font-weight: 600; color: ${C.navy}; width: 50px; }

  /* ── Insight cards ── */
  .insights-col { display: flex; flex-direction: column; gap: 12px; }
  .insight-card {
    border-radius: 10px;
    padding: 14px 16px;
    border-left: 3px solid;
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }
  .insight-card.info    { background: #EFF6FF; border-color: #3B82F6; }
  .insight-card.warn    { background: #FFFBEB; border-color: ${C.amber}; }
  .insight-card.success { background: #ECFDF5; border-color: ${C.green}; }
  .insight-card.danger  { background: #FEF2F2; border-color: ${C.red}; }
  .insight-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
  .insight-title { font-weight: 600; font-size: 13px; color: ${C.navy}; margin-bottom: 3px; }
  .insight-text  { font-size: 12px; color: ${C.slate}; line-height: 1.5; }

  /* ── Forecast sparkline ── */
  .forecast-row {
    display: flex;
    align-items: flex-end;
    gap: 4px;
    height: 60px;
    margin-bottom: 8px;
  }
  .forecast-bar {
    flex: 1;
    border-radius: 3px 3px 0 0;
    transition: height 0.6s ease;
    min-height: 4px;
  }
  .forecast-labels {
    display: flex;
    gap: 4px;
    font-size: 10px;
    color: ${C.slate};
  }
  .forecast-labels span { flex: 1; text-align: center; }

  /* ── Churn table ── */
  .churn-list { display: flex; flex-direction: column; gap: 8px; }
  .churn-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid ${C.border};
  }
  .churn-row:last-child { border-bottom: none; }
  .churn-avatar {
    width: 32px; height: 32px; border-radius: 50%;
    background: ${C.mint};
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; color: ${C.teal};
    flex-shrink: 0;
  }
  .churn-name { font-size: 13px; font-weight: 500; color: ${C.navy}; }
  .churn-days { font-size: 12px; color: ${C.slate}; }
  .churn-risk {
    margin-left: auto;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
  }
  .risk-high   { background: #FEE2E2; color: ${C.red}; }
  .risk-medium { background: #FFFBEB; color: ${C.amber}; }

  /* ── Model badge ── */
  .model-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: ${C.mint};
    color: ${C.teal};
    font-size: 11px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 20px;
    border: 1px solid ${C.teal2};
  }

  /* ── Full width forecast ── */
  .forecast-full {
    background: ${C.white};
    border-radius: 14px;
    border: 1px solid ${C.border};
    padding: 20px;
    margin-bottom: 24px;
  }
  .forecast-full-bars {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    height: 80px;
    margin-bottom: 8px;
  }
  .forecast-full-bar {
    flex: 1;
    border-radius: 4px 4px 0 0;
    transition: height 0.6s ease;
    cursor: pointer;
    position: relative;
  }
  .forecast-full-bar:hover { opacity: 0.8; }
  .forecast-full-labels {
    display: flex;
    gap: 6px;
    font-size: 10px;
    color: ${C.slate};
  }
  .forecast-full-labels span { flex: 1; text-align: center; }
`;

// ── Data ──────────────────────────────────────────────
const PREDICT_OPTIONS = [
  { id: "demand",   icon: "📦", title: "Daily Demand Forecast",    desc: "How much food to prep each day" },
  { id: "staffing", icon: "👥", title: "Staff Scheduling",         desc: "How many staff to schedule per shift" },
  { id: "churn",    icon: "📉", title: "Customer Churn Prediction", desc: "Which regulars are at risk of leaving" },
  { id: "revenue",  icon: "💰", title: "Revenue Forecasting",       desc: "Predict weekly and monthly revenue" },
];

const DATA_SOURCES = [
  { id: "csv",     icon: "📄", title: "Upload CSV",        sub: "Export from any POS system" },
  { id: "square",  icon: "◼️", title: "Connect Square",    sub: "Auto-sync daily sales data" },
  { id: "toast",   icon: "🍞", title: "Connect Toast",     sub: "Auto-sync daily sales data" },
  { id: "shopify", icon: "🛍️", title: "Connect Shopify",   sub: "For online + in-store orders" },
];

const PIPELINE_STEPS = [
  "Validating your data",
  "Running data wrangling pipeline",
  "Engineering demand features",
  "Training XGBoost model",
  "Training Prophet time series model",
  "Azure AutoML selecting best model",
  "Deploying prediction endpoint",
  "Generating your dashboard",
];

const FORECAST_DATA = [
  { day: "Mon", value: 62, color: "#0D9488" },
  { day: "Tue", value: 58, color: "#0D9488" },
  { day: "Wed", value: 71, color: "#0D9488" },
  { day: "Thu", value: 85, color: "#0D9488" },
  { day: "Fri", value: 95, color: "#14B8A6" },
  { day: "Sat", value: 100, color: "#14B8A6" },
  { day: "Sun", value: 78, color: "#0D9488" },
  { day: "Mon", value: 60, color: "#94A3B8" },
  { day: "Tue", value: 55, color: "#94A3B8" },
  { day: "Wed", value: 68, color: "#94A3B8" },
  { day: "Thu", value: 80, color: "#94A3B8" },
  { day: "Fri", value: 92, color: "#94A3B8" },
  { day: "Sat", value: 98, color: "#94A3B8" },
  { day: "Sun", value: 74, color: "#94A3B8" },
];

const TOP_ITEMS = [
  { name: "Classic Burger",      pct: 88 },
  { name: "BBQ Ribs",            pct: 76 },
  { name: "Mac & Cheese",        pct: 71 },
  { name: "Buffalo Wings",       pct: 58 },
  { name: "Caesar Salad",        pct: 45 },
];

const CHURN_LIST = [
  { name: "Table 14 Regular", days: 52, risk: "high",   initials: "TR" },
  { name: "Friday Group",     days: 44, risk: "high",   initials: "FG" },
  { name: "Lunch Couple",     days: 38, risk: "medium", initials: "LC" },
  { name: "Weekly Family",    days: 35, risk: "medium", initials: "WF" },
  { name: "Tuesday Solo",     days: 31, risk: "medium", initials: "TS" },
];

// ── Components ────────────────────────────────────────
function Navbar() {
  return (
    <nav className="nav">
      <div className="nav-logo">
        🔮 Sense<span>Cast</span>
        <span className="nav-badge">BETA</span>
      </div>
      <div style={{ fontSize: 13, color: C.slate }}>
        sensecast.ai
      </div>
    </nav>
  );
}

function Step1({ selected, onSelect, onNext }) {
  return (
    <div className="onboard-wrap">
      <div className="onboard-card">
        <div className="step-indicator">
          <div className="step-dot active" />
          <div className="step-dot" />
          <div className="step-dot" />
        </div>
        <div className="step-label">Step 1 of 3</div>
        <div className="step-title">What do you want to predict?</div>
        <div className="step-sub">
          Choose what matters most to your restaurant right now. 
          SenseCast will train the right ML model for your specific need.
        </div>
        <div className="options-grid">
          {PREDICT_OPTIONS.map(opt => (
            <div
              key={opt.id}
              className={`option-card ${selected === opt.id ? "selected" : ""}`}
              onClick={() => onSelect(opt.id)}
            >
              <div className="option-icon">{opt.icon}</div>
              <div>
                <div className="option-title">{opt.title}</div>
                <div className="option-desc">{opt.desc}</div>
              </div>
              <div className="option-check">
                {selected === opt.id && "✓"}
              </div>
            </div>
          ))}
        </div>
        <button
          className="btn-primary"
          disabled={!selected}
          onClick={onNext}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

function Step2({ selected, onSelect, onNext }) {
  return (
    <div className="onboard-wrap">
      <div className="onboard-card">
        <div className="step-indicator">
          <div className="step-dot done" />
          <div className="step-dot active" />
          <div className="step-dot" />
        </div>
        <div className="step-label">Step 2 of 3</div>
        <div className="step-title">How do you want to provide your data?</div>
        <div className="step-sub">
          Connect your POS system or upload a CSV export. 
          SenseCast securely processes your historical sales data.
        </div>
        <div className="source-grid">
          {DATA_SOURCES.map(src => (
            <div
              key={src.id}
              className={`source-card ${selected === src.id ? "selected" : ""}`}
              onClick={() => onSelect(src.id)}
            >
              <div className="source-emoji">{src.icon}</div>
              <div className="source-title">{src.title}</div>
              <div className="source-sub">{src.sub}</div>
            </div>
          ))}
        </div>
        {selected === "csv" && (
          <div
            className="upload-zone uploaded"
            onClick={() => {}}
          >
            <div className="upload-icon">✅</div>
            <div className="upload-title">sensecast_restaurant_data.csv</div>
            <div className="upload-sub">176,473 rows · 2 years of data · Ready to train</div>
          </div>
        )}
        {selected && selected !== "csv" && (
          <div className="upload-zone uploaded">
            <div className="upload-icon">🔗</div>
            <div className="upload-title">{DATA_SOURCES.find(s=>s.id===selected)?.title} Connected</div>
            <div className="upload-sub">Syncing last 24 months of sales data...</div>
          </div>
        )}
        {!selected && (
          <div style={{height: 16}} />
        )}
        <button
          className="btn-primary"
          disabled={!selected}
          onClick={onNext}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

function Step3({ onNext }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [seats, setSeats] = useState("");

  return (
    <div className="onboard-wrap">
      <div className="onboard-card">
        <div className="step-indicator">
          <div className="step-dot done" />
          <div className="step-dot done" />
          <div className="step-dot active" />
        </div>
        <div className="step-label">Step 3 of 3</div>
        <div className="step-title">Tell us about your restaurant</div>
        <div className="step-sub">
          A few details help SenseCast personalise predictions 
          for your specific business context.
        </div>
        <div className="input-group">
          <label className="input-label">Restaurant Name</label>
          <input
            className="input-field"
            placeholder="e.g. Main Street Grill"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <div className="input-group">
          <label className="input-label">Cuisine Type</label>
          <input
            className="input-field"
            placeholder="e.g. American, Italian, Mexican"
            value={type}
            onChange={e => setType(e.target.value)}
          />
        </div>
        <div className="input-group" style={{marginBottom: 32}}>
          <label className="input-label">Number of Seats</label>
          <input
            className="input-field"
            placeholder="e.g. 80"
            value={seats}
            onChange={e => setSeats(e.target.value)}
          />
        </div>
        <button
          className="btn-primary"
          onClick={onNext}
        >
          🚀 Train My Model
        </button>
      </div>
    </div>
  );
}

function Training({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(p => {
        const next = Math.min(p + 1.2, 100);
        setCurrentStep(Math.floor((next / 100) * PIPELINE_STEPS.length));
        if (next >= 100) { clearInterval(interval); setTimeout(onDone, 600); }
        return next;
      });
    }, 80);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="training-wrap">
      <div className="training-card">
        <div className="training-spinner" />
        <div className="training-title">Training Your Model</div>
        <div className="training-sub">
          Azure AutoML is testing XGBoost, Prophet, and LightGBM 
          on your data and selecting the most accurate model.
        </div>
        <div className="progress-bar-bg">
          <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
        </div>
        <div style={{ fontSize: 12, color: C.slate, textAlign: "right" }}>
          {Math.round(progress)}%
        </div>
        <div className="progress-steps">
          {PIPELINE_STEPS.map((step, i) => (
            <div
              key={i}
              className={`progress-step ${i < currentStep ? "done" : i === currentStep ? "active" : ""}`}
            >
              <div className="step-status">
                {i < currentStep ? "✓" : i === currentStep ? "·" : ""}
              </div>
              {step}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const maxVal = Math.max(...FORECAST_DATA.map(d => d.value));

  return (
    <div className="dash-wrap">
      <div className="dash-header">
        <div>
          <div className="dash-title">Main Street Grill — SenseCast Dashboard</div>
          <div className="dash-sub">AI-powered predictions · Updated daily at midnight</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="model-badge">
            ⚡ XGBoost · R² 0.91
          </div>
          <div className="last-updated">Last updated: Today 00:01</div>
        </div>
      </div>

      {/* Metrics */}
      <div className="metrics-row">
        <div className="metric-card">
          <div className="metric-label">Tomorrow's Covers</div>
          <div className="metric-value">847</div>
          <div className="metric-delta delta-up">↑ 12% vs last week</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Prep Level</div>
          <div className="metric-value" style={{color: C.teal}}>High</div>
          <div className="metric-delta delta-up">↑ 40% above normal</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Peak Hour</div>
          <div className="metric-value">12pm</div>
          <div className="metric-delta delta-neutral">→ Lunch rush</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Churn Risk</div>
          <div className="metric-value" style={{color: C.red}}>23</div>
          <div className="metric-delta delta-down">↑ 3 new this week</div>
        </div>
      </div>

      {/* 14-day forecast */}
      <div className="forecast-full">
        <div className="card-title">
          📈 14-Day Demand Forecast
          <span style={{fontSize:11, color:C.slate, fontWeight:400, marginLeft:"auto"}}>
            Solid = this week · Faded = next week
          </span>
        </div>
        <div className="forecast-full-bars">
          {FORECAST_DATA.map((d, i) => (
            <div
              key={i}
              className="forecast-full-bar"
              style={{
                height: `${(d.value / maxVal) * 100}%`,
                background: d.color,
                opacity: i >= 7 ? 0.4 : 1,
              }}
            />
          ))}
        </div>
        <div className="forecast-full-labels">
          {FORECAST_DATA.map((d, i) => (
            <span key={i} style={{opacity: i >= 7 ? 0.5 : 1}}>{d.day}</span>
          ))}
        </div>
      </div>

      {/* Two column */}
      <div className="dash-grid">
        {/* Top items forecast */}
        <div className="dash-card">
          <div className="card-title">🍽️ Tomorrow's Top Items</div>
          <div className="bar-chart">
            {TOP_ITEMS.map((item, i) => (
              <div key={i} className="bar-row">
                <div className="bar-label">{item.name}</div>
                <div className="bar-bg">
                  <div
                    className="bar-fill"
                    style={{
                      width: `${item.pct}%`,
                      background: `linear-gradient(90deg, ${C.teal}, ${C.teal2})`,
                    }}
                  />
                </div>
                <div className="bar-value">{item.pct} covers</div>
              </div>
            ))}
          </div>
        </div>

        {/* Plain English Insights */}
        <div className="dash-card">
          <div className="card-title">💡 Plain English Insights</div>
          <div className="insights-col">
            <div className="insight-card success">
              <div className="insight-icon">📈</div>
              <div>
                <div className="insight-title">Strong Weekend Ahead</div>
                <div className="insight-text">Saturday predicted 40% above your weekly average. Schedule full staff by 11am.</div>
              </div>
            </div>
            <div className="insight-card warn">
              <div className="insight-icon">⚠️</div>
              <div>
                <div className="insight-title">Slow Monday Incoming</div>
                <div className="insight-text">Next Monday is 30% below average. Consider a lunch special to drive covers.</div>
              </div>
            </div>
            <div className="insight-card info">
              <div className="insight-icon">🌦️</div>
              <div>
                <div className="insight-title">Weather Impact</div>
                <div className="insight-text">Rain forecast Thursday — indoor dining usually lifts 15% on wet days.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Churn alerts */}
        <div className="dash-card">
          <div className="card-title">🚨 Churn Alerts — At-Risk Regulars</div>
          <div className="churn-list">
            {CHURN_LIST.map((c, i) => (
              <div key={i} className="churn-row">
                <div className="churn-avatar">{c.initials}</div>
                <div>
                  <div className="churn-name">{c.name}</div>
                  <div className="churn-days">Last visit {c.days} days ago</div>
                </div>
                <div
                  className={`churn-risk ${c.risk === "high" ? "risk-high" : "risk-medium"}`}
                >
                  {c.risk === "high" ? "High Risk" : "Medium"}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Model info */}
        <div className="dash-card">
          <div className="card-title">🤖 Your Model</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { label: "Best Model",       value: "XGBoost Regressor", color: C.teal },
              { label: "Accuracy (R²)",    value: "0.91 — Excellent",  color: C.green },
              { label: "MAE",              value: "842 covers",        color: C.navy },
              { label: "Training Data",    value: "2 years · 59K orders", color: C.navy },
              { label: "Top Feature",      value: "7-day rolling avg", color: C.navy },
              { label: "Retrained",        value: "Daily at midnight", color: C.slate },
            ].map((row, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                <span style={{ color: C.slate }}>{row.label}</span>
                <span style={{ fontWeight: 600, color: row.color }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────
export default function App() {
  const [screen, setScreen]     = useState("step1"); // step1|step2|step3|training|dashboard
  const [predict, setPredict]   = useState(null);
  const [source, setSource]     = useState(null);

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <Navbar />
        {screen === "step1"    && <Step1 selected={predict} onSelect={setPredict} onNext={() => setScreen("step2")} />}
        {screen === "step2"    && <Step2 selected={source}  onSelect={setSource}  onNext={() => setScreen("step3")} />}
        {screen === "step3"    && <Step3 onNext={() => setScreen("training")} />}
        {screen === "training" && <Training onDone={() => setScreen("dashboard")} />}
        {screen === "dashboard"&& <Dashboard />}
      </div>
    </>
  );
}
